/*
 * Purchase picker — custom element for the Shower Filter Annual Pack PDP.
 * Renders a Rorra-style 3-option radio stack consolidating variant + selling-plan
 * selection. Drives Shopify's native Loop subscription widget rather than bypassing
 * it, so Loop's own JS handles writing `selling_plan` to the form (and fires its
 * analytics / lifecycle hooks).
 *
 * On selection:
 *  1. Updates the parent form's hidden `id` input (variant). Loop doesn't manage variants.
 *  2. Programmatically clicks Loop's subscribe / onetime radio inside .loop-widget-container
 *     so Loop's bundle takes care of the selling-plan side.
 *  3. Dispatches `variant:change` on the form so existing price / buy-button listeners
 *     react. detail.variant.featured_media is null so the existing product-gallery
 *     handler is a no-op — we manage gallery image swaps ourselves below.
 *  4. If the selected radio has `data-replacement-image`, hides all <product-gallery>
 *     media slides (and their thumbnail buttons) and injects a single replacement image
 *     into the carousel. Restores when another option is selected.
 */

class PurchasePicker extends HTMLElement {
  constructor() {
    super();
    this.onChange = this.onChange.bind(this);
  }

  connectedCallback() {
    this.form = this._findForm();
    if (!this.form) return;

    this.idInput = this.form.elements.id;
    this.gallery = this._findGallery();
    this.replacementSlide = null;
    this._loopPriceCache = {};

    this.addEventListener('change', this.onChange);

    // Apply the initially-checked radio so the form starts in a consistent state
    const initial = this.querySelector('input[type="radio"]:checked');
    if (initial) this._applyWhenLoopReady(initial);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    if (this._priceObserver) {
      this._priceObserver.disconnect();
      this._priceObserver = null;
    }
  }

  onChange(event) {
    if (event.target && event.target.matches('input[type="radio"]')) {
      this._apply(event.target);
    }
  }

  _apply(radio) {
    if (!radio || radio.disabled) return;

    const variantId = radio.dataset.variantId || '';
    const sellingPlanId = radio.dataset.sellingPlanId || '';
    const replacementImage = radio.dataset.replacementImage || '';

    if (this.idInput) this.idInput.value = variantId;

    // Drive Loop's widget so it writes selling_plan to the form (and fires its
    // own analytics / lifecycle hooks).
    this._syncLoopWidget(sellingPlanId);

    // Notify other components (price block, buy buttons) about the variant change.
    // Omit featured_media so the existing product-gallery handler is a no-op —
    // we manage the gallery image ourselves below.
    this.form.dispatchEvent(
      new CustomEvent('variant:change', {
        bubbles: true,
        detail: {
          variant: {
            id: variantId ? Number(variantId) : null,
            featured_media: null,
          },
        },
      })
    );

    // Some price-listener implementations watch the input itself.
    if (this.idInput) {
      this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this._swapGalleryImage(replacementImage);
    this._applyCachedPrices();
  }

  _applyWhenLoopReady(initial, attempts = 0) {
    // Loop's widget loads asynchronously. Defer the initial apply until it's
    // mounted, otherwise our .click() fires before Loop's listeners exist.
    // Poll up to ~2 seconds (40 × 50ms), then give up gracefully.
    const loopReady = !!document.querySelector(
      '.loop-widget-container [data-loop-widget-onetime-purchase-option]'
    );
    if (loopReady || attempts > 40) {
      this._apply(initial);
      if (loopReady) this._initLoopPriceSync(initial);
      return;
    }
    setTimeout(() => this._applyWhenLoopReady(initial, attempts + 1), 50);
  }

  async _initLoopPriceSync(initialRadio) {
    // Capture Loop's prices for each variant by briefly switching variants.
    // Loop's widget is CSS-hidden on this PDP (Phase 2) so the user doesn't
    // see any flicker. We restore the initial radio's state at the end.
    const variantIds = [...new Set(
      [...this.querySelectorAll('input[type="radio"][data-variant-id]')]
        .map((r) => r.dataset.variantId)
        .filter(Boolean)
    )];

    for (const variantId of variantIds) {
      if (this.idInput) this.idInput.value = variantId;
      this.form.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: { id: Number(variantId), featured_media: null } },
        })
      );
      // Give Loop a tick to update its DOM after the variant change
      await new Promise((r) => setTimeout(r, 60));
      this._syncPricesFromLoop();
    }

    // Restore the initial radio's state (variant + selling plan + image)
    if (initialRadio) this._apply(initialRadio);

    // Watch for any further Loop price changes and propagate
    this._observeLoopPrices();
  }

  _observeLoopPrices() {
    const widget = document.querySelector('.loop-widget-container');
    if (!widget) return;

    if (this._priceObserver) this._priceObserver.disconnect();
    this._priceObserver = new MutationObserver(() => this._syncPricesFromLoop());

    widget.querySelectorAll('.loop-widget-purchase-option-price, .loop-widget-purchase-option-compare-at-price').forEach((el) => {
      this._priceObserver.observe(el, { childList: true, characterData: true, subtree: true });
    });
  }

  _syncPricesFromLoop() {
    const widget = document.querySelector('.loop-widget-container');
    if (!widget) return;

    const subOpt = widget.querySelector('[data-loop-widget-selling-plan-group]');
    const oneOpt = widget.querySelector('[data-loop-widget-onetime-purchase-option]');

    const readPrice = (root, sel) => {
      const el = root?.querySelector(sel);
      if (!el) return null;
      // Skip if visually hidden via the Loop class
      if (el.classList.contains('loop-display-none')) return null;
      const txt = el.textContent?.trim();
      return txt || null;
    };

    const subPrice = readPrice(subOpt, '.loop-widget-purchase-option-price');
    const subCompare = readPrice(subOpt, '.loop-widget-purchase-option-compare-at-price');
    const onePrice = readPrice(oneOpt, '.loop-widget-purchase-option-price');
    const oneCompare = readPrice(oneOpt, '.loop-widget-purchase-option-compare-at-price');

    const currentVariantId = this.idInput?.value;
    if (!currentVariantId) return;

    if (!this._loopPriceCache[currentVariantId]) this._loopPriceCache[currentVariantId] = {};
    const cache = this._loopPriceCache[currentVariantId];
    if (subPrice) cache.sub = subPrice;
    if (subCompare) cache.subCompare = subCompare;
    if (onePrice) cache.one = onePrice;
    if (oneCompare) cache.oneCompare = oneCompare;

    this._applyCachedPrices();
  }

  _applyCachedPrices() {
    this.querySelectorAll('input[type="radio"][data-variant-id]').forEach((radio) => {
      const cache = this._loopPriceCache[radio.dataset.variantId];
      if (!cache) return;

      const card = radio.closest('label');
      const priceEl = card?.querySelector('.purchase-picker__price-current');
      const strikeEl = card?.querySelector('.purchase-picker__price-strike');

      const isSub = !!radio.dataset.sellingPlanId;
      const newPrice = isSub ? cache.sub : cache.one;
      const newStrike = isSub ? cache.subCompare : cache.oneCompare;

      if (priceEl && newPrice) priceEl.textContent = newPrice;
      if (strikeEl && newStrike) strikeEl.textContent = newStrike;
    });
  }

  _syncLoopWidget(sellingPlanId) {
    // Find the Loop widget; if it's not on the page, no-op.
    const widget = document.querySelector('.loop-widget-container');
    if (!widget) return;

    // Loop's purchase options are role="radio" elements identified by data attributes:
    //   [data-loop-widget-onetime-purchase-option] — the one-time radio
    //   [data-loop-widget-selling-plan-group]       — the subscribe radio (carries the
    //                                                 selling plan group id)
    // Calling .click() on either invokes Loop's own click handler so it writes
    // selling_plan to the form and fires its lifecycle hooks.
    const target = sellingPlanId
      ? widget.querySelector('[data-loop-widget-selling-plan-group]')
      : widget.querySelector('[data-loop-widget-onetime-purchase-option]');
    if (target) target.click();
  }

  _swapGalleryImage(url) {
    if (!this.gallery) return;
    const carousel = this.gallery.querySelector('scroll-carousel') ||
      this.gallery.querySelector('.product-gallery__carousel');
    if (!carousel) return;

    const originalSlides = carousel.querySelectorAll(
      '.product-gallery__media:not([data-purchase-picker-injected])'
    );

    if (url) {
      originalSlides.forEach((el) => {
        el.dataset.purchasePickerHidden = '1';
        el.style.display = 'none';
      });
      if (!this.replacementSlide) {
        this.replacementSlide = document.createElement('div');
        this.replacementSlide.className =
          'product-gallery__media snap-center is-initial';
        this.replacementSlide.dataset.purchasePickerInjected = '1';
        this.replacementSlide.dataset.mediaType = 'image';
        this.replacementSlide.setAttribute('role', 'group');
        const img = document.createElement('img');
        img.alt = '';
        img.loading = 'eager';
        this.replacementSlide.appendChild(img);
        carousel.prepend(this.replacementSlide);
      }
      const img = this.replacementSlide.querySelector('img');
      if (img) {
        img.src = url;
        img.removeAttribute('srcset');
      }
      this.replacementSlide.style.display = '';

      // Hide the carousel-navigation thumbnail buttons so orphaned thumbnails
      // for the original media aren't visible while only our slide is shown.
      const nav = this.gallery.querySelector('carousel-navigation');
      if (nav) {
        nav.querySelectorAll('button.scroll-marker').forEach((btn) => {
          btn.dataset.purchasePickerHidden = '1';
          btn.hidden = true;
        });
      }
    } else {
      originalSlides.forEach((el) => {
        if (el.dataset.purchasePickerHidden) {
          delete el.dataset.purchasePickerHidden;
          el.style.display = '';
        }
      });
      if (this.replacementSlide && this.replacementSlide.parentNode) {
        this.replacementSlide.parentNode.removeChild(this.replacementSlide);
      }
      this.replacementSlide = null;

      const nav = this.gallery.querySelector('carousel-navigation');
      if (nav) {
        nav.querySelectorAll('button[data-purchase-picker-hidden]').forEach((btn) => {
          delete btn.dataset.purchasePickerHidden;
          btn.hidden = false;
        });
      }
    }
  }

  _findForm() {
    const id = this.dataset.formId;
    if (id) {
      const f = document.getElementById(id);
      if (f) return f;
    }
    return this.closest('form');
  }

  _findGallery() {
    const id = this.dataset.formId;
    if (id) {
      const bound = document.querySelector(
        'product-gallery[form="' + id + '"]'
      );
      if (bound) return bound;
    }
    return document.querySelector('product-gallery');
  }
}

if (!customElements.get('purchase-picker')) {
  customElements.define('purchase-picker', PurchasePicker);
}
